import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Mail, CheckCircle, AlertCircle, Loader2, UserPlus } from 'lucide-react';

export function AcceptInvite() {
  const { inviteId } = useParams<{ inviteId: string }>();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteData, setInviteData] = useState<any>(null);
  const [householdData, setHouseholdData] = useState<any>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    loadInviteData();
  }, [inviteId]);

  async function loadInviteData() {
    try {
      setLoading(true);
      setError('');

      if (!inviteId) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      // Get invitation details
      const inviteDoc = await getDoc(doc(db, 'invites', inviteId));
      
      if (!inviteDoc.exists()) {
        setError('Invitation not found or has expired');
        setLoading(false);
        return;
      }

      const invite = inviteDoc.data();

      // Check if already accepted
      if (invite.status === 'accepted') {
        setError('This invitation has already been accepted');
        setLoading(false);
        return;
      }

      // Check if declined
      if (invite.status === 'declined') {
        setError('This invitation was declined');
        setLoading(false);
        return;
      }

      setInviteData({ id: inviteDoc.id, ...invite });

      // Get household details
      const householdDoc = await getDoc(doc(db, 'households', invite.householdId));
      if (householdDoc.exists()) {
        setHouseholdData({ id: householdDoc.id, ...householdDoc.data() });
      }

      setLoading(false);

      // If user is logged in, automatically try to accept
      if (user) {
        await handleAcceptInvite();
      }
    } catch (err: any) {
      console.error('Error loading invite:', err);
      setError('Failed to load invitation details');
      setLoading(false);
    }
  }

  async function handleAcceptInvite() {
    if (!user) {
      return;
    }

    try {
      setProcessing(true);
      setError('');

      // Warn if email doesn't match, but allow acceptance
      if (user.email !== inviteData?.email) {
        console.log(
          `Note: Invitation was sent to ${inviteData?.email}, but accepting as ${user.email}`
        );
      }

      // Call Cloud Function to accept invitation (bypasses Firestore rules)
      const functions = getFunctions();
      const acceptInvitation = httpsCallable(functions, 'acceptInvitation');
      
      const result = await acceptInvitation({ inviteId });
      const data = result.data as any;

      if (data.success) {
        setSuccess(true);
        setProcessing(false);

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        throw new Error(data.message || 'Failed to accept invitation');
      }

    } catch (err: any) {
      console.error('Error accepting invite:', err);
      
      // Handle Cloud Function errors
      if (err.code === 'permission-denied') {
        // Shouldn't happen since we check email first, but handle as fallback
        setIsLoggingOut(true);
        setError(
          `Wrong email. Logging you out so you can sign up with ${inviteData?.email}...`
        );
        setTimeout(async () => {
          await logout();
          setError('');
          setIsLoggingOut(false);
          setProcessing(false);
        }, 2000);
      } else if (err.code === 'already-exists') {
        setError('This invitation has already been accepted');
        setProcessing(false);
      } else if (err.code === 'not-found') {
        setError('Invitation not found');
        setProcessing(false);
      } else if (err.code === 'failed-precondition') {
        setError(err.message || 'Cannot accept this invitation');
        setProcessing(false);
      } else {
        setError(err.message || 'Failed to accept invitation. Please try again.');
        setProcessing(false);
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-green animate-spin" />
      </div>
    );
  }

  // DON'T show error screen if we're in the process of logging out
  if (error && !isLoggingOut) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Invitation Error</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/"
              className="inline-block bg-primary-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-green/90 transition-all"
            >
              Go to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show logging out message
  if (isLoggingOut) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Wrong Email Detected</h1>
            <p className="text-gray-600 mb-2">{error}</p>
            <p className="text-sm text-gray-500">You'll be able to sign up with the correct email in a moment...</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Welcome to the Household!</h1>
            <p className="text-gray-600 mb-6">
              You've successfully joined <strong>{householdData?.name}</strong>
            </p>
            <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // User is not logged in - show signup/login options
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="FridgeLife" className="w-20 h-20 mx-auto mb-3" />
            <h1 className="text-3xl font-bold text-primary-green mb-2">Household Invitation</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="w-16 h-16 bg-primary-mint rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-primary-green" />
            </div>

            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                You've Been Invited!
              </h2>
              <p className="text-gray-600 mb-4">
                You've been invited to join <strong>{householdData?.name}</strong>
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-900">
                  <strong>Invitation sent to:</strong><br />
                  {inviteData?.email}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3 text-center">
                  To accept this invitation, please create an account or log in:
                </p>
                <Link
                  to={`/signup?invite=${inviteId}&email=${encodeURIComponent(inviteData?.email || '')}`}
                  className="w-full bg-primary-green text-white py-3 rounded-xl font-semibold hover:bg-primary-green/90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary-green/30"
                >
                  <UserPlus className="w-5 h-5" />
                  Create Account
                </Link>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or</span>
                </div>
              </div>

              <Link
                to={`/login?invite=${inviteId}`}
                className="w-full bg-white text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all flex items-center justify-center gap-2 border-2 border-gray-200"
              >
                Log In
              </Link>
            </div>

            <p className="text-xs text-gray-500 mt-6 text-center">
              Make sure to use the email address <strong>{inviteData?.email}</strong> when creating your account
            </p>
          </div>
        </div>
      </div>
    );
  }

  // User is logged in but hasn't accepted yet
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
          <div className="w-16 h-16 bg-primary-mint rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-primary-green" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2">Accept Household Invitation</h2>
          <p className="text-gray-600 mb-6">
            Join <strong>{householdData?.name}</strong> and start managing food together!
          </p>

          <button
            onClick={handleAcceptInvite}
            disabled={processing}
            className="w-full bg-primary-green text-white py-3 rounded-xl font-semibold hover:bg-primary-green/90 disabled:opacity-50 transition-all shadow-lg shadow-primary-green/30"
          >
            {processing ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Accepting...
              </span>
            ) : (
              'Accept Invitation'
            )}
          </button>

          <Link
            to="/dashboard"
            className="block mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            Maybe later
          </Link>
        </div>
      </div>
    </div>
  );
}
